import { useEffect, useMemo, useState } from 'react'
import { useAuth } from './auth/AuthProvider'
import { Login } from './pages/Login'
import { ForcePasswordChange } from './pages/ForcePasswordChange'
import { Workspace } from './pages/Workspace'
import { Admin } from './pages/Admin'
import { MyResults } from './pages/MyResults'
import { AtivosCx } from './modules/ativos-cx/AtivosCx'
import { AnnouncementsAdmin } from './pages/admin/AnnouncementsAdmin'
import { HighlightsAdmin } from './pages/admin/HighlightsAdmin'
import { LastUpdateCard, useLatestReleaseInfo } from './components/LastUpdateCard'
import { AnnouncementBell } from './components/AnnouncementBell'
import { StartupLoadingGate } from './components/StartupLoadingGate'

type Route = 'cx' | 'workspace' | 'results' | 'admin' | 'comunicados' | 'destaques'
type NavIconName = 'cx' | 'workspace' | 'results' | 'admin' | 'comunicados' | 'destaques' | 'ferias' | 'external'

const VACATION_PORTAL_URL = 'https://acompanhamento-ferias.pages.dev/'

function NavIcon({ name }: { name: NavIconName }) {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true }
  if (name === 'cx') return <svg {...common}><path d="M5 4h4l2 5-2 2a14 14 0 0 0 4 4l2-2 5 2v4a2 2 0 0 1-2 2C10 21 3 14 3 6a2 2 0 0 1 2-2Z"/></svg>
  if (name === 'workspace') return <svg {...common}><path d="M4 10.5 12 4l8 6.5"/><path d="M6.5 9.5V20h11V9.5"/><path d="M10 20v-6h4v6"/></svg>
  if (name === 'results') return <svg {...common}><path d="M5 20V11"/><path d="M12 20V4"/><path d="M19 20v-7"/><path d="M3 20h18"/></svg>
  if (name === 'admin') return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.86 2.86-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1v.1h-4v-.1a1.7 1.7 0 0 0-1.4-1.6 1.7 1.7 0 0 0-1.88.34l-.06.06-2.86-2.86.06-.06A1.7 1.7 0 0 0 3.8 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1-.4h-.1v-4h.1A1.7 1.7 0 0 0 3.8 8a1.7 1.7 0 0 0-.34-1.88l-.06-.06L6.26 3.2l.06.06A1.7 1.7 0 0 0 8.2 3.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1v-.1h4v.1A1.7 1.7 0 0 0 15 3.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.86 2.86-.06.06A1.7 1.7 0 0 0 19.4 8c.12.38.33.72.6 1 .28.27.62.48 1 .6h.1v4H21a1.7 1.7 0 0 0-1.6 1.4Z"/></svg>
  if (name === 'comunicados') return <svg {...common}><path d="M4 13v-2l13-5v12L4 13Z"/><path d="M7 14.2 8 19h3l-1-5.9"/><path d="M19 9.5a3.5 3.5 0 0 1 0 5"/></svg>
  if (name === 'destaques') return <svg {...common}><circle cx="12" cy="8" r="4"/><path d="m9.4 11.1-2 9 4.6-2.8 4.6 2.8-2-9"/></svg>
  if (name === 'ferias') return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4M17 3v4M3 10h18"/><path d="m9 15 2 2 4-4"/></svg>
  return <svg {...common}><path d="M7 17 17 7M8 7h9v9"/></svg>
}

function getInitialRoute(): Route {
  if (window.location.pathname.startsWith('/ativos-cx')) return 'cx'
  if (window.location.pathname.startsWith('/meus-resultados')) return 'results'
  if (window.location.pathname.startsWith('/comunicados')) return 'comunicados'
  if (window.location.pathname.startsWith('/destaques')) return 'destaques'
  return window.location.pathname.startsWith('/admin') ? 'admin' : 'workspace'
}

function AppContent() {
  const { session, profile, loading, offlineProfile, signOut } = useAuth()
  const canLoadRelease = !loading &&
    Boolean(session?.access_token) &&
    Boolean(session?.user.id) &&
    profile?.id === session?.user.id &&
    profile?.ativo === true &&
    session?.user.app_metadata?.must_change_password !== true
  const { info: latestRelease, offline: latestReleaseOffline } = useLatestReleaseInfo(canLoadRelease)
  const [route, setRoute] = useState<Route>(getInitialRoute)

  useEffect(() => {
    const onPopState = () => setRoute(getInitialRoute())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const canAdmin = useMemo(() => profile?.role === 'administrador' || profile?.role === 'gestao', [profile])

  useEffect(() => {
    if (profile && (route === 'admin' || route === 'comunicados' || route === 'destaques') && !canAdmin) {
      window.history.replaceState({}, '', '/')
      setRoute('workspace')
    }
  }, [profile, route, canAdmin])

  function navigate(next: Route) {
    const path = next === 'cx' ? '/ativos-cx' : next === 'admin'
      ? '/admin'
      : next === 'results'
        ? '/meus-resultados'
        : next === 'comunicados'
          ? '/comunicados'
          : next === 'destaques'
            ? '/destaques'
            : '/'
    window.history.pushState({}, '', path)
    setRoute(next)
  }

  if (loading) return <main className="center-screen"><div className="loader-card">Carregando Suite...</div></main>
  if (!session) return <Login />
  if (session.user.app_metadata?.must_change_password === true) return <ForcePasswordChange session={session} />

  if (!profile) {
    return <main className="center-screen"><section className="loader-card"><h1>Acesso não liberado</h1><p className="muted">Sua conta autenticou, mas não possui um perfil ativo no Suite.</p><button className="primary-button" onClick={() => signOut()}>Sair</button></section></main>
  }

  if (!profile.ativo) {
    return <main className="center-screen"><section className="loader-card"><h1>Acesso desativado</h1><p className="muted">Procure o administrador do Suite para reativar seu acesso.</p><button className="primary-button" onClick={() => signOut()}>Sair</button></section></main>
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark small">AGB</div>
          <div><p className="eyebrow">CENTRAL AGB</p><strong>Suite de Procedimentos</strong><span>versão {latestRelease?.version ?? '—'}</span></div>
        </div>

        <nav className="main-nav" aria-label="Navegação principal">
          <button className={route === 'cx' ? 'active' : ''} onClick={() => navigate('cx')}><span><NavIcon name="cx" /></span><div><strong>Ativos CX</strong><small>Carteira e negociações</small></div></button>
          <button className={route === 'workspace' ? 'active' : ''} onClick={() => navigate('workspace')}>
            <span><NavIcon name="workspace" /></span><div><strong>Atendimento</strong><small>Consultas e procedimentos</small></div>
          </button>
          {!canAdmin && <button className={route === 'results' ? 'active' : ''} onClick={() => navigate('results')}>
            <span><NavIcon name="results" /></span><div><strong>Meus Resultados</strong><small>Indicadores e evolução mensal</small></div>
          </button>}
          {canAdmin && <button className={route === 'admin' ? 'active' : ''} onClick={() => navigate('admin')}>
            <span><NavIcon name="admin" /></span><div><strong>Administração</strong><small>Bases, releases e auditoria</small></div>
          </button>}
          {canAdmin && <button className={route === 'comunicados' ? 'active' : ''} onClick={() => navigate('comunicados')}>
            <span><NavIcon name="comunicados" /></span><div><strong>Comunicados</strong><small>Publicação e acompanhamento</small></div>
          </button>}
          {canAdmin && <button className={route === 'destaques' ? 'active' : ''} onClick={() => navigate('destaques')}>
            <span><NavIcon name="destaques" /></span><div><strong>Destaques</strong><small>Reconhecimento da equipe</small></div>
          </button>}
        </nav>

        <section className="sidebar-collaborator" aria-label="Serviços do colaborador">
          <p>COLABORADOR</p>
          <a href={VACATION_PORTAL_URL} target="_blank" rel="noopener noreferrer">
            <span className="sidebar-collaborator__icon" aria-hidden="true"><NavIcon name="ferias" /></span>
            <span className="sidebar-collaborator__copy"><strong>Solicitar férias</strong><small>Abrir controle de férias</small></span>
            <span className="sidebar-collaborator__external" aria-hidden="true"><NavIcon name="external" /></span>
          </a>
        </section>

        <LastUpdateCard info={latestRelease} offline={latestReleaseOffline} />

        <div className="sidebar-user">
          {offlineProfile && <span className="offline-badge">Perfil em cache</span>}
          <strong>{profile.nome || profile.email}</strong><span>{profile.email}</span><span className="role-label">{profile.role}</span><button onClick={() => signOut()}>Sair</button>
        </div>
      </aside>

      <main className="main-area">
        <AnnouncementBell />
        {offlineProfile && <div className="offline-banner" role="status">Sem comunicação com o banco. O Suite está usando o último perfil validado e poderá consultar bases salvas em cache.</div>}
        {route === 'cx'
          ? <AtivosCx />
          : route === 'results'
          ? <MyResults />
          : route === 'admin' && canAdmin
            ? <Admin />
            : route === 'comunicados' && canAdmin
              ? <section className="workspace-content"><AnnouncementsAdmin /></section>
              : route === 'destaques' && canAdmin
                ? <section className="workspace-content"><HighlightsAdmin /></section>
                : <Workspace />}
      </main>
    </div>
  )
}

export default function App() {
  const { loading } = useAuth()
  return <StartupLoadingGate ready={!loading}><AppContent /></StartupLoadingGate>
}
